// utils.ts
import {
  BasicMeetingData,
  Meeting,
  OptionsAnswer,
  TemplateBlock,
  TemplateBlockAndAnswer,
  TemplateBlockAnswer,
  TemplateElements
} from '../types';

/**
 * Formats a time with hours and minutes to a string with AM/PM based on user's locale
 */
export const formatTimeWithAmPm = (hours: number, minutes: number): string => {
  // Create a new date object with today's date but with the provided hours and minutes
  const date = new Date();
  date.setHours(hours);
  date.setMinutes(minutes);
  date.setSeconds(0);

  // Format the time with AM/PM using user's locale
  return date.toLocaleTimeString('en-US', {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Parses a time string and returns it formatted with AM/PM based on user's locale
 * Can handle various time formats like "14:30", "2:30", etc.
 */
export const parseAndFormatTimeString = (timeStr: string): string => {
  // Try to extract hours and minutes from the time string
  const timeRegex = /(\d{1,2})[:\s](\d{2})/;
  const match = timeStr.match(timeRegex);

  if (match) {
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    return formatTimeWithAmPm(hours, minutes);
  }

  // If we couldn't parse the time, return the original string
  return timeStr;
};

export const classNames = (...classes: (string | boolean | undefined)[]) =>
  classes.filter(Boolean).join(' ');

export const getInitials = (fullName: string): string => {
  const names = fullName.split(' ');
  const initials = names.map((name) => name.charAt(0).toUpperCase()).join('');
  return initials;
};

/**
 * Converts enum values into a readable strings
 */
export const getReadableString = (templateElement: TemplateElements): string => {
  switch (templateElement) {
    case TemplateElements.SingleSelect:
      return 'Single-Select';
    case TemplateElements.MultiSelect:
      return 'Multi-Select';
    case TemplateElements.ShortAnswer:
      return 'Short Answer';
    case TemplateElements.ShortAnswerList:
      return 'Short Answer List';
    case TemplateElements.Paragraph:
      return 'Paragraph ';
    case TemplateElements.Date:
      return 'Date';
    case TemplateElements.Time:
      return 'Time';
    case TemplateElements.Calculation:
      return 'Calculation';
  }
};

/**
 * Combines arrays of template blocks and answers together
 * into an object {block, answer}
 */
export const zipTemplateBlocksAndAnswers = (
  blocks: TemplateBlock[],
  answers: TemplateBlockAnswer[]
): TemplateBlockAndAnswer[] => {
  return (
    blocks
      // Combine blocks with their answers
      .map((block: TemplateBlock) => {
        const answer = answers.find(
          (answer: TemplateBlockAnswer) => block.id === answer.templateBlockId
        );
        return { block, answer: answer };
      })
  );
};

const mapCounterToChar = (counter: number): string => {
  return String.fromCharCode(97 + counter);
};
const mapQuestionAndAnswerToText = (blockAndAnswer: TemplateBlockAndAnswer): string => {
  switch (blockAndAnswer.block.type) {
    case 'Calculation':
      return `${blockAndAnswer.block.question}\n  ${blockAndAnswer.answer?.answer}`;
    case 'ShortAnswer':
      return `${blockAndAnswer.block.question}\n  ${blockAndAnswer.answer?.answer}`;
    case 'ShortAnswerList': {
      const shortAnswerList = blockAndAnswer.answer;
      const shortAnswerText: string[] = [];
      let counter = 0;
      if (shortAnswerList?.answer && Array.isArray(shortAnswerList.answer)) {
        for (const shortAnswer of shortAnswerList.answer) {
          shortAnswerText.push(`  ${mapCounterToChar(counter++)}. ${shortAnswer}`);
        }
      }
      return `${blockAndAnswer.block.question}\n${shortAnswerText.join('\n')}`;
    }
    case 'Paragraph':
      return `${blockAndAnswer.block.question}\n${blockAndAnswer.answer?.answer}`;
    case 'Date':
      return `${blockAndAnswer.block.question}\n  ${blockAndAnswer.answer?.answer}`;
    case 'Time':
      return `${blockAndAnswer.block.question}\n  ${blockAndAnswer.answer?.answer}`;
    case 'SingleSelect': {
      if (blockAndAnswer.answer?.answer === null) {
        return '';
      }
      const singleSelectText: string[] = [];
      for (const option of blockAndAnswer.block.optArgs?.options || []) {
        const answer = blockAndAnswer.answer?.answer as OptionsAnswer;
        const isSelectedText = answer[option.trim()] ? '[ x ]' : '[  ]';
        singleSelectText.push(`  ${isSelectedText} ${option}`);
      }
      return `${blockAndAnswer.block.question}\n${singleSelectText.join('\n')}`;
    }
    case 'MultiSelect': {
      const answer = blockAndAnswer.answer?.answer as OptionsAnswer;
      if (answer === null) {
        return '';
      }
      const multiSelectText: string[] = [];
      for (const option of blockAndAnswer.block.optArgs?.options || []) {
        const isSelectedText = answer[option.trim()] ? '[ x ]' : '[  ]';
        multiSelectText.push(`  ${isSelectedText} ${option}`);
      }
      return `${blockAndAnswer.block.question}\n${multiSelectText.join('\n')}`;
    }
    default:
      return '';
  }
};

const buildMetadataText = (meetingData: BasicMeetingData): string => {
  const formattedDate = meetingData.date;
  /* 
  .toLocaleDateString('en-US', {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Uses the user's local time zone
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });*/

  // Format the time string to include AM/PM based on user's locale
  const formattedTime = parseAndFormatTimeString(meetingData.time);
  /*.toLocaleTimeString('en-US', {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Uses the user's local time zone
    timeZoneName: 'short' // Display the short time zone name, e.g., "PST"
  });*/

  let meetingDataText = '';
  meetingDataText += `Client: ${meetingData.client.name}\n`;
  meetingDataText += `Template: ${meetingData.template.title}\n`;
  meetingDataText += `Date: ${formattedDate}\n`;
  meetingDataText += `Time: ${formattedTime}\n`;
  return meetingDataText;
};

/**
 * Formats a list of meeting note blocks into a string
 */
export const formatMeetingNoteText = (
  meetingData: BasicMeetingData | undefined,
  meetingNoteBlocks: TemplateBlockAndAnswer[]
): string => {
  let meetingDataText = '';
  if (meetingData) {
    meetingDataText = buildMetadataText(meetingData);
  }
  const meetingNoteText = meetingNoteBlocks
    .map((blockAndAnswer, index) => {
      return `${index + 1}. ${mapQuestionAndAnswerToText(blockAndAnswer)}\n`;
    })
    .join('\n');
  return `${meetingDataText}\n\n${meetingNoteText}`;
};

/**
 * Sorts a list of meetings. If descending = true, sorts from future to past.
 * If false, sorts from past to future
 */
export const sortMeetings = (meetings: Meeting[], descending = true) =>
  meetings.sort((a: Meeting, b: Meeting) => {
    const dateTimeA = a.dateTime.getTime();
    const dateTimeB = b.dateTime.getTime();
    const createdAtA = a.createdAt!.getTime();
    const createdAtB = b.createdAt!.getTime();

    if (dateTimeA === dateTimeB) {
      // If dateTime is the same, use createdAt as tiebreaker
      return descending ? createdAtB - createdAtA : createdAtA - createdAtB;
    }

    return descending ? dateTimeB - dateTimeA : dateTimeA - dateTimeB;
  });

/**
 * Validates a redirect URL against a whitelist of allowed paths.
 * Prevents open redirect attacks by only allowing specific internal paths.
 *
 * @param url - The URL to validate (should be a relative path)
 * @returns The validated URL if safe, null otherwise
 */
export const validateRedirectUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;

  // Must be a relative path starting with /
  if (!url.startsWith('/')) {
    return null;
  }

  // Prevent protocol-relative URLs (e.g., //evil.com)
  if (url.startsWith('//')) {
    return null;
  }

  // Whitelist of allowed redirect paths
  const allowedPaths = [
    '/', // Home page
    '/templates/preview/' // Template preview pages
  ];

  // Check if URL matches any allowed path
  const isAllowed = allowedPaths.some((path) => {
    if (path === '/') {
      return url === '/';
    }
    return url.startsWith(path);
  });

  return isAllowed ? url : null;
};
